import EnterpriseWebsiteExperiments from 'bundles/epic/clients/EnterpriseWebsite';
import {
  DEFAULT_LICENSE_COUNT,
  MIN_LICENSE_COUNT_CONTROL,
  MIN_LICENSE_COUNT_TEST,
} from 'bundles/wes-common/constants/billing';

/**
 * Whether we already recorded an EPIC impression for `minm2Licenses` in this document.
 * Keeps `EnterpriseWebsiteExperiments.get('minm2Licenses')` to a single call per page load even if
 * multiple bundles or React Strict Mode invoke {@link generateImpressionForMinm2Licenses} more than once.
 */
let hasRecordedMinm2LicensesImpression = false;

/**
 * Records a `minm2Licenses` allocation impression (first call in this document only).
 * Subsequent calls read the variant via `preview` only.
 *
 * Prefer invoking once per product area that needs allocation (e.g. teams checkout shell or
 * enterprise manage-licenses via {@link getMinLicenseCount}(true)), and use {@link getMinLicenseCount}(false)
 * or {@link previewMinm2Licenses} elsewhere.
 */
export function generateImpressionForMinm2Licenses(): boolean {
  if (hasRecordedMinm2LicensesImpression) {
    return EnterpriseWebsiteExperiments.preview('minm2Licenses');
  }
  hasRecordedMinm2LicensesImpression = true;
  return EnterpriseWebsiteExperiments.get('minm2Licenses');
}

export function previewMinm2Licenses(): boolean {
  return EnterpriseWebsiteExperiments.preview('minm2Licenses');
}

/**
 * Effective minimum license count for WES self-serve checkout and manage-licenses flows.
 * When `minm2Licenses` is on, uses {@link MIN_LICENSE_COUNT_TEST}; when off, {@link MIN_LICENSE_COUNT_CONTROL}.
 */
export function getMinLicenseCount(generateImpression = false): number {
  const enabled = generateImpression ? generateImpressionForMinm2Licenses() : previewMinm2Licenses();
  return enabled ? MIN_LICENSE_COUNT_TEST : MIN_LICENSE_COUNT_CONTROL;
}

/**
 * Default `learnerCount` when absent from the URL (e.g. `/purchase/plan` redirect).
 * Uses {@link MIN_LICENSE_COUNT_TEST} when `minm2Licenses` preview is on; otherwise {@link DEFAULT_LICENSE_COUNT}.
 * Does not record an EPIC impression — use {@link generateImpressionForMinm2Licenses} or {@link getMinLicenseCount}(true) where appropriate.
 */
export function getDefaultCheckoutLearnerCount(): number {
  return previewMinm2Licenses() ? MIN_LICENSE_COUNT_TEST : DEFAULT_LICENSE_COUNT;
}
